import { DotMatrix } from '@clickguard/design-system'
import { useEffect, useRef, useState, useCallback } from 'react'
import { FiArrowRight } from 'react-icons/fi'

const HUBSPOT_MEETING_URL =
	'https://meetings.hubspot.com/thabisile-khumalo/demo?embed=true'
const HUBSPOT_EMBED_SCRIPT =
	'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js'
const LOGO_URL =
	'https://storage.googleapis.com/clickguard-storage-dev/public/assets/img/Clickguard%20Logo%20New.svg'

interface BookDemoStepProps {
	onDemoBooked: (meetingDate?: string) => void
	onSkip: () => void | Promise<void>
}

export const BookDemoStep = ({ onDemoBooked, onSkip }: BookDemoStepProps) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const [isBooked, setIsBooked] = useState(false)
	const [isSkipping, setIsSkipping] = useState(false)

	const handleSkip = async () => {
		if (isSkipping) return
		setIsSkipping(true)
		await onSkip()
	}

	// Load HubSpot meetings embed script
	useEffect(() => {
		// Set the data-src on the container before loading the script
		if (containerRef.current) {
			containerRef.current.setAttribute('data-src', HUBSPOT_MEETING_URL)
		}

		// Check if script is already loaded
		const existingScript = document.querySelector(
			`script[src="${HUBSPOT_EMBED_SCRIPT}"]`
		)
		if (existingScript) {
			// Script already loaded — re-trigger by removing and re-adding
			existingScript.remove()
		}

		const script = document.createElement('script')
		script.src = HUBSPOT_EMBED_SCRIPT
		script.type = 'text/javascript'
		script.async = true
		document.body.appendChild(script)

		return () => {
			// Cleanup script on unmount
			const scriptEl = document.querySelector(
				`script[src="${HUBSPOT_EMBED_SCRIPT}"]`
			)
			if (scriptEl) scriptEl.remove()
		}
	}, [])

	// Listen for HubSpot meeting booked event
	const handleMessage = useCallback(
		(event: MessageEvent) => {
			if (event.origin !== 'https://meetings.hubspot.com') return

			// HubSpot sends a message when a meeting is successfully booked
			if (
				event.data?.meetingBookSucceeded ||
				(typeof event.data === 'string' &&
					event.data.includes('meetingBookSucceeded'))
			) {
				const meetingDate =
					event.data?.meetingDate || event.data?.meeting?.startTime || undefined
				setIsBooked(true)
				onDemoBooked(meetingDate)
			}
		},
		[onDemoBooked]
	)

	useEffect(() => {
		window.addEventListener('message', handleMessage)
		return () => window.removeEventListener('message', handleMessage)
	}, [handleMessage])

	return (
		<div className="flex md:flex-row flex-col min-h-screen">
			{/* Left Panel */}
			<div className="flex flex-col md:gap-6 gap-3 xl:pl-10 md:pl-6 pl-4 md:pr-0 pr-4 md:justify-between justify-start md:shrink-0">
				{/* Logo */}
				<div className="md:pt-10 pt-6">
					<img src={LOGO_URL} alt="ClickGuard" className="h-6 w-[156px]" />
				</div>
				{/* Content */}
				<div className="flex flex-col gap-6 min-h-0 md:max-w-[310px]">
					{/* Header & Description */}
					<div className="flex flex-col gap-1">
						<h1 className="font-grotesk font-medium text-[30px] leading-[36px] text-t-default">
							Book a demo to see ClickGuard in action
						</h1>
						<div className="text-[14px] leading-[18px] text-t-desc-gray">
							<p className="mb-3.5">
								Here's what others have taken away from the call:
							</p>
							<ul className="list-disc ml-[21px] space-y-0">
								<li>
									How to protect budgets and improve ROAS without touching your
									campaigns
								</li>
								<li>Where ClickGuard fits in your existing ad stack</li>
								<li>
									Full visibility into real-time fraud activity and optimization
									opportunities
								</li>
							</ul>
						</div>
					</div>

					{/* Skip CTA — hidden after booking */}
					{!isBooked && (
						<div className="flex flex-row items-center gap-1">
							<span className="text-[14px] leading-[18px] text-t-default">
								No time for a call?
							</span>
							<button
								type="button"
								onClick={handleSkip}
								disabled={isSkipping}
								className={`flex items-center gap-0.5 ${
									isSkipping
										? 'opacity-60 cursor-not-allowed'
										: 'cursor-pointer'
								}`}
							>
								{isSkipping ? (
									<DotMatrix size="xs" tone="inherit" className="flex-shrink-0" />
								) : (
									<>
										<span className="font-grotesk font-medium text-[14px] leading-[18px] text-brand-tint-text">
											{isSkipping
												? 'Loading plans...'
												: 'Skip ahead to see plans'}
										</span>
										<FiArrowRight className="w-5 h-5 text-brand-tint-text" />
									</>
								)}
							</button>
						</div>
					)}
				</div>
				<div className="md:block hidden" />
			</div>
			{/* Right Panel — Iframe */}
			<div className="flex w-full items-center justify-center min-w-0 md:p-6 p-4">
				<div className="flex justify-center items-center">
					<div
						ref={containerRef}
						className="meetings-iframe-container xl:w-[900px] lg:w-[680px] md:w-[430px] w-screen"
					/>
				</div>
			</div>
		</div>
	)
}
